import {
  type TemplateConfig,
  getOverlayClass,
  getImagePositionClass,
} from '@/mocks/template-config';
import type { CompanyData } from '@/hooks/useCompanyData';

interface TopSectionProps {
  config: TemplateConfig['top'];
  companyData?: CompanyData;
  bannerMobile?: string;
}

export default function TopSection({ config, companyData, bannerMobile }: TopSectionProps) {
  const cData = companyData || {
    name: 'Eletrônica 2000',
    foundedYear: 1993,
  } as CompanyData;

  const alignmentClass = config.alignment === 'left' ? 'items-start' : 'items-center';
  const textAlignClass = config.alignment === 'left' ? 'text-left' : 'text-center';

  return (
    <div className="relative">
      {/* Background Image - Desktop */}
      <div className="absolute inset-0 hidden md:block">
        <img
          src={config.bannerImage}
          alt="Banner"
          className={`w-full h-full object-cover ${getImagePositionClass('center')}`}
        />
        {config.overlay !== 'none' && (
          <div className={`absolute inset-0 ${getOverlayClass(config.overlay)}`} />
        )}
      </div>

      {/* Background Image - Mobile */}
      <div className="absolute inset-0 md:hidden">
        <img
          src={bannerMobile || config.bannerImage}
          alt="Banner"
          className={`w-full h-full object-cover ${getImagePositionClass('center')}`}
        />
        {config.overlay !== 'none' && (
          <div className={`absolute inset-0 ${getOverlayClass(config.overlay)}`} />
        )}
      </div>

      {/* Content */}
      <div className={`relative z-10 flex flex-col ${alignmentClass} pt-8 md:pt-12 px-4`}>
        {/* Logo */}
        {config.showLogo && (
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 md:w-16 md:h-16 rounded-full border-2 border-brand-gold flex items-center justify-center">
              <div className="w-8 h-8 md:w-11 md:h-11 rounded-full border border-brand-gold flex items-center justify-center">
                <div className="w-3 h-3 md:w-4 md:h-4 bg-brand-gold rounded-full" />
              </div>
            </div>
            {config.showCompanyName && (
              <div className="flex flex-col">
                <span className="text-white text-2xl md:text-3xl font-bold tracking-wide">
                  {cData.name.split(' ')[0]}{' '}
                  <span className="text-brand-gold">{cData.name.split(' ')[1]}</span>
                </span>
              </div>
            )}
          </div>
        )}

        {/* Desde 1993 */}
        {config.showFounded && (
          <div className={`flex items-center gap-3 mb-4 ${config.alignment === 'center' ? 'justify-center' : ''}`}>
            <div className="w-16 md:w-24 h-px bg-brand-gold" />
            <span className="text-brand-gold text-xs md:text-sm tracking-[0.3em] uppercase font-medium">
              Desde {cData.foundedYear}
            </span>
            <div className="w-16 md:w-24 h-px bg-brand-gold" />
          </div>
        )}

        {/* Title */}
        {config.showTitle && (
          <h1 className={`font-serif text-white text-3xl md:text-5xl lg:text-6xl italic tracking-wide ${textAlignClass} mb-2`}>
            {config.title}
          </h1>
        )}

        {/* Subtitle */}
        {config.showSubtitle && (
          <p className={`text-brand-gold text-sm md:text-lg tracking-wide ${textAlignClass} mb-8`}>
            {config.subtitle}
          </p>
        )}

        {/* Divider */}
        <div className="w-32 md:w-48 h-px bg-brand-gold/50 mb-6" />
      </div>
    </div>
  );
}